import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/InvitesPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Invite {
  invite_id: number;
  code: string;
  org_id: number;
  is_owner: boolean;
  sent_to_email: string | null;
  status: string;
}

const InvitesPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Invites", "/invites");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchInvites = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/invites`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch invites.");
          return;
        }

        const data = await response.json();
        setInvites(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchInvites();
  }, [token, selectedOrgID]);

  const handleDelete = async (inviteID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/invites/${inviteID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete invite.");
        return;
      }

      // Update the invites list after deletion
      setInvites(invites.filter((invite) => invite.invite_id !== inviteID));
    } catch (err) {
      console.error("Error deleting invite:", err);
    }
  };

  const navigateToCreate = () => {
    navigate("/invites/create");
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  const getStatusClassName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return styles.statusPending;
      case 'accepted':
        return styles.statusAccepted;
      case 'expired':
        return styles.statusExpired;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Invites</h1>
      <button onClick={navigateToCreate} className={styles.createButton}>
        Create Invite
      </button>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Code</th>
            <th className={styles.tableHeader}>Org ID</th>
            <th className={styles.tableHeader}>Is Owner</th>
            <th className={styles.tableHeader}>Sent To</th>
            <th className={styles.tableHeader}>Status</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invites.length > 0 ? (
            invites.map((invite) => (
              <tr key={invite.invite_id} className={styles.tableRow}>
                <td className={styles.tableCell}>{invite.code}</td>
                <td className={styles.tableCell}>{invite.org_id}</td>
                <td className={styles.tableCell}>
                  {invite.is_owner ? (
                    <span className={styles.ownerBadge}>Yes</span>
                  ) : (
                    "No"
                  )}
                </td>
                <td className={styles.tableCell}>{invite.sent_to_email || "N/A"}</td>
                <td className={`${styles.tableCell} ${getStatusClassName(invite.status)}`}>
                  {invite.status}
                </td>
                <td className={styles.tableCell}>
                  <button 
                    onClick={() => handleDelete(invite.invite_id)} 
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className={styles.tableCell}>No invites found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InvitesPage;
